import { buildHwApiUrl } from "@hunkware-ui/common";

import { HW_API_URL } from "../../configs";
import { fetcher } from "../../libs";

import { calculateQuoteViaNode } from "./calculate-quote-via-node";
import { phpRemoveNulls } from "./php-remove-nulls";
import {
  QuoteCalculateParams,
  QuoteCalculateResponse,
  QuoteCreateParams,
  QuoteCreateResponse,
} from "./types";

/**
 * Quote create, on php-api or — with `useNode` (`HunkWare_Node_Version_Save_Quote`,
 * docs/migrations/website-account-quote-crud/07-frontend-migration.md) — on hunkware-api.
 *
 * Both branches go through the shared `fetcher`: an absolute URL makes axios ignore the
 * php-api `baseURL` while both interceptors still run. That is load-bearing on the request
 * side — the Node route rewrites the body in php-api's spelling (`force_hunks`, `notes.size`,
 * …) exactly as the PHP wrapper does, so the body must reach it snake_cased — and it keeps
 * the response camelCasing and the Bugsnag error path identical. The Node route hangs off the
 * bare `/api` mount, with no `/v1`.
 */
export const quoteCreate = ({
  locationId,
  accountId,
  payload,
  token,
  useNode = false,
}: QuoteCreateParams) => {
  const config = {
    // trimResponse (booking-v4-only) hides pricing/internal fields the
    // customer shouldn't see from the response — see quotes.post.php.
    params: { token, trimResponse: true },
  };

  if (!useNode) {
    return fetcher.post<QuoteCreateResponse>(
      `/locations/${locationId}/accounts/${accountId}/quotes`,
      payload,
      config,
    );
  }

  return fetcher
    .post<QuoteCreateResponse>(
      buildHwApiUrl(
        HW_API_URL,
        `/api/public/website/locations/${locationId}/accounts/${accountId}/quotes`,
      ),
      payload,
      config,
    )
    .then((response) => ({ ...response, data: phpRemoveNulls(response.data) }));
};

export const quoteCalculate = ({
  locationId,
  accountId,
  payload,
  token,
  useNode,
}: QuoteCalculateParams) =>
  useNode
    ? calculateQuoteViaNode({ locationId, accountId, payload, token })
    : fetcher.post<QuoteCalculateResponse>(
        `/locations/${locationId}/accounts/${accountId}/quotes/calculate`,
        payload,
        {
          // newBooking signals the backend to run the new-online-booking
          // calculate path (see apps/php-api calculate.post.php), same as
          // booking-v3 and customer-portal. trimResponse is booking-v4-only
          // (customer-portal doesn't send it) — hides pricing/internal fields
          // the customer shouldn't see from the response.
          params: { token, newBooking: true, trimResponse: true },
        },
      );
